import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { RefreshCw, Send, CheckCircle2, Clock, FileText, Package, AlertTriangle } from 'lucide-react'
import { ApiClientError } from '@/lib/api-client'
import { artistFetch } from '@/lib/artist-api'
import { PageHeader } from '@/components/PageHeader'
import { AsyncBoundary } from '@/components/AsyncBoundary'
import { PerformanceScopeBanner } from '@/components/PerformanceScopeBanner'
import { usePerformanceScope } from '@/lib/use-performance-scope'
import { cn } from '@/lib/utils'

// ── Types ────────────────────────────────────────────────────────────────────

interface Show {
  id: string
  title: string
  date: string
  venue: string
  city: string
  country: string
  stage: string
}

type QuestionnaireStatus = 'draft' | 'sent' | 'completed'
type AdvancingDecision =
  | 'pending'
  | 'venue_provides'
  | 'not_available'
  | 'substitution_review'
  | 'courtesy_available'
  | 'insufficient'

interface AdvancingStatus {
  id: string
  status: QuestionnaireStatus
  deadline: string
  sentAt: string | null
  completedAt: string | null
  venueContactEmail: string
  formToken: string
  _count: { results: number }
  resultSummary: Record<AdvancingDecision, number>
}

interface RiderItem {
  id: string
  department: string
  name: string
  specification: string
  quantity: number
  category: string
  substitutionPolicy?: string
  notes: string | null
}

interface AdvancingResult {
  id: string
  riderItemId: string
  decision: AdvancingDecision
  reason: string | null
  venueResponse: string | null
  venueSpec: Record<string, unknown> | null
  reviewRequired: boolean
  reviewedBy: string | null
  reviewedAt: string | null
  reviewDecision: string | null
  hireTriggered: boolean
  assignedReviewer: string
  riderItem: RiderItem
}

interface QuestionnaireWithResults {
  id: string
  status: QuestionnaireStatus
  deadline: string
  sentAt: string | null
  completedAt: string | null
  venueContactEmail: string
  results: AdvancingResult[]
}

type ManifestDisposition = 'travels' | 'hire_local' | 'venue_provides'

interface ManifestItem {
  id: string
  name: string
  department: string
  disposition: ManifestDisposition
  quantity: number
  weightKg: number | null
  volumeM3: number | null
  hireSupplier: string | null
  notes: string | null
}

interface EquipmentManifest {
  id: string
  showId: string
  totalWeightKg: number | null
  totalVolumeM3: number | null
  confirmedAt: string | null
  confirmedBy: string | null
  items: ManifestItem[]
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function advancingLabel(
  status: QuestionnaireStatus | null,
  resultSummary?: Record<string, number>,
): string {
  if (!status) return 'Not started'
  if (status === 'draft') return 'Not started'
  if (status === 'sent') return 'Questionnaire sent'
  if (status === 'completed') {
    const needsReview = (resultSummary?.substitution_review ?? 0) + (resultSummary?.courtesy_available ?? 0)
    if (needsReview > 0) return 'Response received'
    return 'Reviewed'
  }
  return status
}

function advancingColour(label: string): string {
  switch (label) {
    case 'Not started':
      return 'bg-slate-100 text-slate-600'
    case 'Questionnaire sent':
      return 'bg-blue-100 text-blue-700'
    case 'Response received':
      return 'bg-amber-100 text-amber-700'
    case 'Reviewed':
      return 'bg-green-100 text-green-700'
    default:
      return 'bg-slate-100 text-slate-600'
  }
}

const DECISION_COLOURS: Record<AdvancingDecision, string> = {
  pending: 'bg-slate-100 text-slate-600',
  venue_provides: 'bg-green-100 text-green-700',
  not_available: 'bg-red-100 text-red-700',
  substitution_review: 'bg-amber-100 text-amber-700',
  courtesy_available: 'bg-purple-100 text-purple-700',
  insufficient: 'bg-red-100 text-red-700',
}

const DISPOSITION_LABELS: Record<ManifestDisposition, string> = {
  travels: 'Travels with us',
  hire_local: 'Hire locally',
  venue_provides: 'Venue provides',
}

const DISPOSITION_COLOURS: Record<ManifestDisposition, string> = {
  travels: 'bg-blue-100 text-blue-700',
  hire_local: 'bg-amber-100 text-amber-700',
  venue_provides: 'bg-green-100 text-green-700',
}

// ── Main page ────────────────────────────────────────────────────────────────

export function AdvancingPage() {
  const scope = usePerformanceScope()
  const [selectedShowId, setSelectedShowId] = useState<string | null>(null)

  const { data: shows, error, isLoading } = useQuery<Show[], ApiClientError>({
    queryKey: ['advancing-shows', ...scope.queryKeySuffix],
    queryFn: () =>
      artistFetch<Show[]>(`/shows?stage=confirmed,advancing${scope.toQueryString().replace('?', '&')}`),
  })
  const scopedShows = scope.filterByShowIds(shows ?? [], (s) => s.id)

  useEffect(() => {
    if (!scope.showId) return
    setSelectedShowId(scope.showId)
  }, [scope.showId])

  return (
    <div>
      <PageHeader
        title="Advancing"
        subtitle="Send technical questionnaires to venues, review responses, and generate equipment manifests."
      />
      <PerformanceScopeBanner />

      <AsyncBoundary isLoading={isLoading} error={error}>
        {scopedShows.length === 0 ? (
          <div className="rounded-md border border-ovation-ink/10 bg-white p-6 text-sm text-ovation-muted">
            No shows in confirmed or advancing stage. Shows appear here once they reach the confirmed stage in the pipeline.
          </div>
        ) : (
          <div className="space-y-3">
            {scopedShows.map((show) => (
              <ShowAdvancingRow
                key={show.id}
                show={show}
                isSelected={selectedShowId === show.id}
                onSelect={() =>
                  setSelectedShowId((prev) =>
                    prev === show.id ? null : show.id,
                  )
                }
              />
            ))}
          </div>
        )}
      </AsyncBoundary>
    </div>
  )
}

// ── Show row with expandable detail ──────────────────────────────────────────

function ShowAdvancingRow({
  show,
  isSelected,
  onSelect,
}: {
  show: Show
  isSelected: boolean
  onSelect: () => void
}) {
  const { data: status } = useQuery<AdvancingStatus | null, ApiClientError>({
    queryKey: ['advancing-status', show.id],
    queryFn: () => artistFetch<AdvancingStatus | null>(`/advancing/shows/${show.id}`),
  })

  const label = advancingLabel(status?.status ?? null, status?.resultSummary)
  const colour = advancingColour(label)

  return (
    <div className="rounded-lg border border-ovation-ink/10 bg-white">
      <button
        type="button"
        onClick={onSelect}
        className="flex w-full items-center justify-between gap-4 p-4 text-left hover:bg-ovation-ink/[0.02]"
      >
        <div className="min-w-0 flex-1">
          <div className="font-display text-lg">{show.title}</div>
          <div className="mt-0.5 text-sm text-ovation-muted">
            {new Date(show.date).toLocaleDateString()} · {show.venue}, {show.city}
          </div>
        </div>
        <span
          className={cn(
            'shrink-0 rounded px-2 py-1 text-xs font-medium uppercase',
            colour,
          )}
        >
          {label}
        </span>
      </button>

      {isSelected && (
        <div className="border-t border-ovation-ink/10 p-4">
          <ShowAdvancingDetail show={show} status={status} />
        </div>
      )}
    </div>
  )
}

// ── Detail panel (the core of the page) ──────────────────────────────────────

function ShowAdvancingDetail({
  show,
  status,
}: {
  show: Show
  status: AdvancingStatus | null | undefined
}) {
  const qc = useQueryClient()

  // Send / Generate + send
  const sendMutation = useMutation({
    mutationFn: () =>
      artistFetch(`/advancing/shows/${show.id}/send`, { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['advancing-status', show.id] })
    },
  })

  // ── No questionnaire yet (draft or null) ───────────────────────────────────
  if (!status || status.status === 'draft') {
    return (
      <div className="space-y-3">
        <p className="text-sm text-ovation-muted">
          No questionnaire has been sent for this show yet. Generate and send it to the venue contact.
        </p>
        {sendMutation.error && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {sendMutation.error instanceof ApiClientError
              ? sendMutation.error.message
              : 'Failed to send questionnaire.'}
          </div>
        )}
        <button
          type="button"
          onClick={() => sendMutation.mutate()}
          disabled={sendMutation.isPending}
          className="flex items-center gap-2 rounded-md bg-ovation-ink px-4 py-2 text-sm font-medium text-ovation-paper hover:bg-ovation-ink/90 disabled:opacity-50"
        >
          <Send className="h-4 w-4" />
          {sendMutation.isPending
            ? 'Sending...'
            : 'Generate & Send Questionnaire'}
        </button>
      </div>
    )
  }

  // ── Questionnaire sent, awaiting response ──────────────────────────────────
  if (status.status === 'sent') {
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2 text-sm">
          <Clock className="h-4 w-4 text-blue-500" />
          <span className="font-medium">Awaiting venue response</span>
        </div>
        <div className="text-sm text-ovation-muted">
          Sent to <span className="font-medium text-ovation-ink">{status.venueContactEmail}</span>{' '}
          on {status.sentAt ? new Date(status.sentAt).toLocaleDateString() : 'unknown date'}.
          Deadline: {new Date(status.deadline).toLocaleDateString()}.
        </div>
        <div className="text-xs text-ovation-muted">
          {status._count.results} item{status._count.results !== 1 ? 's' : ''} in questionnaire.
        </div>
        {sendMutation.error && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {sendMutation.error instanceof ApiClientError
              ? sendMutation.error.message
              : 'Failed to resend.'}
          </div>
        )}
        <button
          type="button"
          onClick={() => sendMutation.mutate()}
          disabled={sendMutation.isPending}
          className="flex items-center gap-2 rounded-md border border-ovation-ink/15 px-3 py-2 text-sm font-medium text-ovation-ink hover:bg-ovation-ink/5 disabled:opacity-50"
        >
          <RefreshCw className={cn('h-4 w-4', sendMutation.isPending && 'animate-spin')} />
          {sendMutation.isPending ? 'Resending...' : 'Resend'}
        </button>
      </div>
    )
  }

  // ── Response received (status === 'completed') ─────────────────────────────
  return <CompletedAdvancingDetail show={show} status={status} />
}

// ── Completed: results + review + manifest ───────────────────────────────────

function CompletedAdvancingDetail({
  show,
  status,
}: {
  show: Show
  status: AdvancingStatus
}) {
  const qc = useQueryClient()
  const [showManifest, setShowManifest] = useState(false)

  const {
    data: questionnaire,
    error: resultsError,
    isLoading: resultsLoading,
  } = useQuery<QuestionnaireWithResults, ApiClientError>({
    queryKey: ['advancing-results', show.id],
    queryFn: () =>
      artistFetch<QuestionnaireWithResults>(
        `/advancing/shows/${show.id}/results`,
      ),
  })

  const results = questionnaire?.results ?? []
  const needsReview = results.filter(
    (r) =>
      r.reviewRequired &&
      !r.reviewedAt &&
      (r.decision === 'substitution_review' || r.decision === 'courtesy_available'),
  )
  const allReviewed = needsReview.length === 0 && results.length > 0

  return (
    <div className="space-y-6">
      {/* Summary bar */}
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <div className="flex items-center gap-1.5">
          {allReviewed ? (
            <CheckCircle2 className="h-4 w-4 text-green-600" />
          ) : (
            <AlertTriangle className="h-4 w-4 text-amber-500" />
          )}
          <span className="font-medium">
            {allReviewed
              ? 'All items reviewed'
              : `${needsReview.length} item${needsReview.length !== 1 ? 's' : ''} need review`}
          </span>
        </div>
        {status.completedAt && (
          <span className="text-ovation-muted">
            Response received {new Date(status.completedAt).toLocaleDateString()}
          </span>
        )}
      </div>

      {/* Results list */}
      <AsyncBoundary isLoading={resultsLoading} error={resultsError}>
        <div className="space-y-2">
          {results.map((result) => (
            <AdvancingResultRow
              key={result.id}
              result={result}
              showId={show.id}
            />
          ))}
        </div>
      </AsyncBoundary>

      {/* Manifest section */}
      <div className="border-t border-ovation-ink/10 pt-4">
        {allReviewed ? (
          <>
            <button
              type="button"
              onClick={() => setShowManifest((v) => !v)}
              className="flex items-center gap-2 text-sm font-medium text-ovation-ink hover:underline"
            >
              <Package className="h-4 w-4" />
              {showManifest ? 'Hide equipment manifest' : 'Show equipment manifest'}
            </button>
            {showManifest && <ManifestSection showId={show.id} />}
          </>
        ) : (
          <p className="text-sm text-ovation-muted">
            Review all flagged items before generating the equipment manifest.
          </p>
        )}
      </div>
    </div>
  )
}

// ── Single result row with review action ─────────────────────────────────────

function AdvancingResultRow({
  result,
  showId,
}: {
  result: AdvancingResult
  showId: string
}) {
  const qc = useQueryClient()
  const isReviewable =
    result.reviewRequired &&
    !result.reviewedAt &&
    (result.decision === 'substitution_review' || result.decision === 'courtesy_available')

  const reviewMutation = useMutation({
    mutationFn: (decision: 'accepted' | 'declined') =>
      artistFetch(`/advancing/results/${result.id}/review`, {
        method: 'PATCH',
        body: { decision },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['advancing-results', showId] })
      qc.invalidateQueries({ queryKey: ['advancing-status', showId] })
    },
  })

  const isHighlighted =
    result.decision === 'substitution_review' ||
    result.decision === 'courtesy_available'

  return (
    <div
      className={cn(
        'rounded-lg border p-4',
        isHighlighted && !result.reviewedAt
          ? 'border-amber-300 bg-amber-50'
          : 'border-ovation-ink/10 bg-white',
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="font-medium">{result.riderItem.name}</span>
            <span className="text-xs text-ovation-muted">
              x{result.riderItem.quantity}
            </span>
            <span className="text-xs text-ovation-muted capitalize">
              {result.riderItem.department.replace(/_/g, ' ')}
            </span>
          </div>
          <div className="mt-1 text-sm text-ovation-muted">
            Required: {result.riderItem.specification}
          </div>
          {result.venueResponse && (
            <div className="mt-1 text-sm">
              Venue says: <span className="font-medium">{result.venueResponse}</span>
            </div>
          )}
          {result.reason && (
            <div className="mt-1 text-xs text-ovation-muted">{result.reason}</div>
          )}
          {result.reviewedAt && (
            <div className="mt-1 text-xs text-green-700">
              Reviewed: {result.reviewDecision === 'accepted' ? 'Accepted' : 'Declined (hire required)'}{' '}
              on {new Date(result.reviewedAt).toLocaleDateString()}
            </div>
          )}
        </div>

        <div className="flex shrink-0 flex-col items-end gap-2">
          <span
            className={cn(
              'rounded px-2 py-0.5 text-[10px] font-medium uppercase',
              DECISION_COLOURS[result.decision] ?? 'bg-slate-100 text-slate-600',
            )}
          >
            {result.decision.replace(/_/g, ' ')}
          </span>

          {isReviewable && (
            <div className="flex gap-1.5">
              <button
                type="button"
                onClick={() => reviewMutation.mutate('accepted')}
                disabled={reviewMutation.isPending}
                className="rounded bg-green-600 px-2 py-1 text-xs font-medium text-white hover:bg-green-700 disabled:opacity-50"
              >
                Accept
              </button>
              <button
                type="button"
                onClick={() => reviewMutation.mutate('declined')}
                disabled={reviewMutation.isPending}
                className="rounded bg-red-600 px-2 py-1 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-50"
              >
                Decline
              </button>
            </div>
          )}
        </div>
      </div>
      {reviewMutation.error && (
        <div className="mt-2 text-xs text-red-600">
          {reviewMutation.error instanceof ApiClientError
            ? reviewMutation.error.message
            : 'Review failed.'}
        </div>
      )}
    </div>
  )
}

// ── Equipment Manifest ───────────────────────────────────────────────────────

function ManifestSection({ showId }: { showId: string }) {
  const qc = useQueryClient()

  const {
    data: manifest,
    error: manifestError,
    isLoading: manifestLoading,
  } = useQuery<EquipmentManifest | null, ApiClientError>({
    queryKey: ['advancing-manifest', showId],
    queryFn: async () => {
      try {
        return await artistFetch<EquipmentManifest>(
          `/advancing/shows/${showId}/manifest`,
        )
      } catch (err) {
        if (err instanceof ApiClientError && err.status === 404) return null
        throw err
      }
    },
  })

  const generateMutation = useMutation({
    mutationFn: () =>
      artistFetch(`/advancing/shows/${showId}/manifest`, { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['advancing-manifest', showId] })
    },
  })

  const confirmMutation = useMutation({
    mutationFn: () =>
      artistFetch(`/advancing/shows/${showId}/manifest/confirm`, {
        method: 'POST',
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['advancing-manifest', showId] })
    },
  })

  return (
    <div className="mt-4 space-y-4">
      <AsyncBoundary isLoading={manifestLoading} error={manifestError}>
        {!manifest ? (
          <div className="space-y-3">
            <p className="text-sm text-ovation-muted">
              No equipment manifest generated yet. Generate one from the completed advancing results.
            </p>
            {generateMutation.error && (
              <div className="text-sm text-red-600">
                {generateMutation.error instanceof ApiClientError
                  ? generateMutation.error.message
                  : 'Failed to generate manifest.'}
              </div>
            )}
            <button
              type="button"
              onClick={() => generateMutation.mutate()}
              disabled={generateMutation.isPending}
              className="flex items-center gap-2 rounded-md bg-ovation-ink px-4 py-2 text-sm font-medium text-ovation-paper hover:bg-ovation-ink/90 disabled:opacity-50"
            >
              <FileText className="h-4 w-4" />
              {generateMutation.isPending
                ? 'Generating...'
                : 'Generate manifest'}
            </button>
          </div>
        ) : (
          <ManifestView
            manifest={manifest}
            onConfirm={() => confirmMutation.mutate()}
            confirmPending={confirmMutation.isPending}
            confirmError={confirmMutation.error}
          />
        )}
      </AsyncBoundary>
    </div>
  )
}

function ManifestView({
  manifest,
  onConfirm,
  confirmPending,
  confirmError,
}: {
  manifest: EquipmentManifest
  onConfirm: () => void
  confirmPending: boolean
  confirmError: Error | null
}) {
  const grouped = new Map<ManifestDisposition, ManifestItem[]>()
  for (const item of manifest.items) {
    const disp = item.disposition as ManifestDisposition
    if (!grouped.has(disp)) grouped.set(disp, [])
    grouped.get(disp)!.push(item)
  }

  const dispositionOrder: ManifestDisposition[] = [
    'travels',
    'hire_local',
    'venue_provides',
  ]

  return (
    <div className="space-y-4">
      {/* Totals */}
      <div className="flex gap-6 text-sm">
        {manifest.totalWeightKg != null && (
          <div>
            Total weight:{' '}
            <span className="font-medium">{manifest.totalWeightKg.toFixed(1)} kg</span>
          </div>
        )}
        {manifest.totalVolumeM3 != null && (
          <div>
            Total volume:{' '}
            <span className="font-medium">{manifest.totalVolumeM3.toFixed(2)} m³</span>
          </div>
        )}
        {manifest.confirmedAt && (
          <div className="flex items-center gap-1 text-green-700">
            <CheckCircle2 className="h-3.5 w-3.5" />
            Confirmed {new Date(manifest.confirmedAt).toLocaleDateString()}
          </div>
        )}
      </div>

      {/* Items grouped by disposition */}
      {dispositionOrder
        .filter((d) => grouped.has(d))
        .map((disposition) => (
          <div key={disposition}>
            <h3 className="mb-2 flex items-center gap-2 text-sm font-medium">
              <span
                className={cn(
                  'rounded px-2 py-0.5 text-xs uppercase',
                  DISPOSITION_COLOURS[disposition],
                )}
              >
                {DISPOSITION_LABELS[disposition]}
              </span>
              <span className="text-ovation-muted">
                ({grouped.get(disposition)!.length} item
                {grouped.get(disposition)!.length !== 1 ? 's' : ''})
              </span>
            </h3>
            <div className="space-y-1.5">
              {grouped.get(disposition)!.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center justify-between rounded-md border border-ovation-ink/10 bg-white px-3 py-2 text-sm"
                >
                  <div className="min-w-0 flex-1">
                    <span className="font-medium">{item.name}</span>
                    <span className="ml-2 text-ovation-muted">x{item.quantity}</span>
                    <span className="ml-2 text-xs capitalize text-ovation-muted">
                      {item.department.replace(/_/g, ' ')}
                    </span>
                    {item.hireSupplier && (
                      <span className="ml-2 text-xs text-amber-700">
                        Supplier: {item.hireSupplier}
                      </span>
                    )}
                  </div>
                  <div className="flex gap-3 text-xs text-ovation-muted">
                    {item.weightKg != null && <span>{item.weightKg} kg</span>}
                    {item.volumeM3 != null && <span>{item.volumeM3} m³</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}

      {/* Confirm button */}
      {!manifest.confirmedAt && (
        <div className="pt-2">
          {confirmError && (
            <div className="mb-2 text-sm text-red-600">
              {confirmError instanceof ApiClientError
                ? confirmError.message
                : 'Failed to confirm manifest.'}
            </div>
          )}
          <button
            type="button"
            onClick={onConfirm}
            disabled={confirmPending}
            className="flex items-center gap-2 rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
          >
            <CheckCircle2 className="h-4 w-4" />
            {confirmPending ? 'Confirming...' : 'Confirm manifest'}
          </button>
        </div>
      )}
    </div>
  )
}
